import React from 'react';
import type { MonthBand } from '../../../hooks/pipelineTracker/remContract';
import { money } from './pipelineParts';

/**
 * Pieces Broker REM and the Board pack share. Nothing here holds or invents a figure.
 */

/**
 * Each band's colour, by its position in the scheme: the first band (usually the no-commission
 * floor) reads grey, and each band above it steps through the brand palette. Cycled for a scheme
 * with more bands than colours.
 */
const BAND_PALETTE: ReadonlyArray<{ bar: string; text: string; border: string; fill: string }> = [
  { bar: '#64748b', text: '#cbd5e1', border: 'rgba(100,116,139,0.45)', fill: 'rgba(100,116,139,0.14)' },
  { bar: '#2563eb', text: '#93c5fd', border: 'rgba(37,99,235,0.45)', fill: 'rgba(37,99,235,0.14)' },
  { bar: '#7c3aed', text: '#c4b5fd', border: 'rgba(124,58,237,0.45)', fill: 'rgba(124,58,237,0.14)' },
  { bar: '#16a34a', text: '#86efac', border: 'rgba(22,163,74,0.45)', fill: 'rgba(22,163,74,0.14)' },
  { bar: '#0891b2', text: '#67e8f9', border: 'rgba(8,145,178,0.45)', fill: 'rgba(8,145,178,0.14)' },
  { bar: '#f59e0b', text: '#fcd34d', border: 'rgba(245,158,11,0.45)', fill: 'rgba(245,158,11,0.14)' },
];

export const bandColour = (index: number): (typeof BAND_PALETTE)[number] =>
  BAND_PALETTE[((index % BAND_PALETTE.length) + BAND_PALETTE.length) % BAND_PALETTE.length];

/** '$3.0M to $5.0M', '$5.0M and above'. Uses the card formatter: bands are read, not audited. */
export function bandRangeLabel(fromCents: number, toCents: number | null): string {
  if (toCents === null) return `${money(fromCents)} and above`;
  return `${money(fromCents)} to ${money(toCents)}`;
}

/** Up to one decimal, trailing zero dropped: 20, 12.5. */
export const pct = (value: number): string =>
  `${Number.isInteger(value) ? value : value.toFixed(1).replace(/\.0$/, '')}%`;

/**
 * What a month's band reads as in a cell. The three non-band states are words, never a rate: a
 * guessed rate for a volume past a closed top band would read as a scheme decision nobody made.
 * `split` adds the word, "Band 2 · 50% split", where there is room for it (the progress panel); the
 * month table's narrow cells keep "Band 2 · 50%".
 */
export const BandTag: React.FC<{ band: MonthBand; split?: boolean }> = ({ band, split }) => {
  if (band.status === 'in_band') {
    const colour = bandColour(band.index);
    return (
      <span
        className="inline-flex items-center whitespace-nowrap rounded-full border px-1.5 py-px text-[0.6875rem] font-semibold"
        style={{ color: colour.text, borderColor: colour.border, background: colour.fill }}
      >
        Band {band.index + 1} &middot; {pct(band.ratePct)}{split ? ' split' : ''}
      </span>
    );
  }
  if (band.status === 'not_banded') {
    return <span className="text-[0.6875rem] font-semibold text-gray-500">not banded</span>;
  }
  const text = band.status === 'no_bands' ? 'no scheme' : band.status === 'above_top' ? 'above top band' : 'negative';
  return <span className="text-[0.6875rem] font-semibold text-amber-300">{text}</span>;
};
